import { CONFIG } from "../config";
import type { AppMode, ConnectionState } from "../types";

export class WebRTCManager {
  private peerConnection: RTCPeerConnection | null = null;
  private pendingIceCandidates: RTCIceCandidate[] = [];
  private processedCandidates: Set<string> = new Set();
  private isConnected: boolean = false;
  private mode: AppMode;

  constructor(mode: AppMode) {
    this.mode = mode;
  }

  // =============================================================================
  // PEER CONNECTION SETUP - Used by both viewer and camera
  // =============================================================================

  async createPeerConnection(
    onTrack?: (event: RTCTrackEvent) => void,
    onConnectionStateChange?: (state: ConnectionState) => void,
    onIceConnectionStateChange?: () => void
  ): Promise<RTCPeerConnection> {
    const configuration: RTCConfiguration = {
      iceServers: [...CONFIG.WEBRTC.ICE_SERVERS],
      iceCandidatePoolSize: CONFIG.WEBRTC.ICE_CANDIDATE_POOL_SIZE,
    };

    this.peerConnection = new RTCPeerConnection(configuration);

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        // Both viewer and camera collect ICE candidates
        this.pendingIceCandidates.push(event.candidate);
      }
    };

    // Handle incoming tracks (viewer only)
    if (onTrack) {
      this.peerConnection.ontrack = onTrack;
    }

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;

      if (state === "connected") {
        this.isConnected = true;
        if (onConnectionStateChange) onConnectionStateChange("connected");
      } else if (state === "connecting") {
        if (onConnectionStateChange) onConnectionStateChange("connecting");
      } else if (state === "disconnected") {
        this.isConnected = false;
        if (onConnectionStateChange) onConnectionStateChange("disconnected");
      } else if (state === "failed") {
        this.isConnected = false;
        if (onConnectionStateChange) onConnectionStateChange("failed");
      }
    };

    // Handle ICE connection state changes
    this.peerConnection.oniceconnectionstatechange = () => {
      const iceState = this.peerConnection?.iceConnectionState;

      if (
        (iceState === "connected" || iceState === "completed") &&
        this.peerConnection?.connectionState !== "connected"
      ) {
        // Manually trigger connected state if ICE is connected but connection state hasn't updated
        this.isConnected = true;
        if (onConnectionStateChange) onConnectionStateChange("connected");
      }

      if (onIceConnectionStateChange) onIceConnectionStateChange();
    };

    // Placeholder handlers for signaling and data channels
    this.peerConnection.onsignalingstatechange = () => {
      // Signaling state tracking for internal use
    };

    this.peerConnection.ondatachannel = (event) => {
      // Data channel handling for internal use
    };

    this.peerConnection.onicegatheringstatechange = () => {
      // ICE gathering state tracking for internal use
    };

    return this.peerConnection;
  }

  // =============================================================================
  // VIEWER METHODS - For receiving video streams
  // =============================================================================

  async createViewerOffer(): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("Peer connection not created");
    }

    // Create a data channel to help with ICE gathering
    this.peerConnection.createDataChannel("test", { ordered: false });

    // Add transceiver to receive video only
    this.peerConnection.addTransceiver("video", { direction: "recvonly" });

    // Create offer
    const offer = await this.peerConnection.createOffer();

    await this.peerConnection.setLocalDescription(offer);

    return offer;
  }

  // =============================================================================
  // CAMERA METHODS - For sending video streams
  // =============================================================================

  async createCameraAnswer(
    offer: RTCSessionDescriptionInit,
    localStream: MediaStream
  ): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("Peer connection not created");
    }

    // Set remote description (offer)
    await this.peerConnection.setRemoteDescription(offer);

    // Add local stream tracks
    localStream.getTracks().forEach((track) => {
      this.peerConnection!.addTrack(track, localStream);
    });

    // Create and set answer
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);

    return answer;
  }

  // =============================================================================
  // SHARED METHODS - Used by both viewer and camera
  // =============================================================================
  async setRemoteAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error("Peer connection not created");
    }

    if (!this.peerConnection.remoteDescription) {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }

  async addIceCandidates(candidates: RTCIceCandidateInit[]): Promise<void> {
    if (!this.peerConnection?.remoteDescription) {
      return; // Can't add candidates without remote description
    }

    for (const candidate of candidates) {
      if (candidate?.candidate) {
        const candidateKey = candidate.candidate;

        // Skip if already processed
        if (this.processedCandidates.has(candidateKey)) {
          continue;
        }

        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          this.processedCandidates.add(candidateKey);
        } catch (error) {
          // Silent error handling
        }
      }
    }
  }

  async waitForICEGathering(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.peerConnection) {
        resolve();
        return;
      }

      // Check if ICE gathering is already complete
      if (this.peerConnection.iceGatheringState === "complete") {
        resolve();
        return;
      }

      // Set timeout
      const timeout = setTimeout(() => {
        resolve(); // Proceed with whatever candidates we have
      }, CONFIG.TIMEOUTS.ICE_GATHERING);

      // Check candidate count periodically
      const checkCandidateCount = () => {
        if (this.pendingIceCandidates.length >= CONFIG.POLLING.MIN_ICE_CANDIDATES) {
          clearTimeout(timeout);
          this.peerConnection?.removeEventListener("icegatheringstatechange", onICEGatheringStateChange);
          resolve();
          return true;
        }
        return false;
      };

      // Listen for ICE gathering completion
      const onICEGatheringStateChange = () => {
        if (this.peerConnection?.iceGatheringState === "complete") {
          clearTimeout(timeout);
          this.peerConnection.removeEventListener("icegatheringstatechange", onICEGatheringStateChange);
          resolve();
        } else {
          checkCandidateCount();
        }
      };

      this.peerConnection.addEventListener("icegatheringstatechange", onICEGatheringStateChange);

      // Initial check
      if (!checkCandidateCount()) {
        // Start periodic check
        const candidateCheckInterval = setInterval(() => {
          if (checkCandidateCount()) {
            clearInterval(candidateCheckInterval);
            clearTimeout(timeout);
          }
        }, 500);

        // Clear interval when timeout hits
        setTimeout(() => clearInterval(candidateCheckInterval), CONFIG.TIMEOUTS.ICE_GATHERING);
      }
    });
  }

  get iceCandidates(): RTCIceCandidate[] {
    return [...this.pendingIceCandidates];
  }

  clearPendingCandidates(): void {
    this.pendingIceCandidates = [];
  }

  get connected(): boolean {
    return this.isConnected;
  }

  get connection(): RTCPeerConnection | null {
    return this.peerConnection;
  }

  close(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.isConnected = false;
    this.pendingIceCandidates = [];
    this.processedCandidates.clear();
  }

  async getConnectionStats(): Promise<RTCStatsReport | null> {
    if (!this.peerConnection) return null;

    try {
      return await this.peerConnection.getStats();
    } catch (error) {
      return null;
    }
  }

  // =============================================================================
  // GETTERS - State access
  // =============================================================================
}
